import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import Select from 'react-select';
import { useDistributors } from '../../../../app/modules/auth';
import { CoordinatorSection } from './CoordinatorSection';
import { ManagerSection } from './ManagerSection';

interface Coordinator {
  name: string;
  email: string;
}

interface Manager {
  name: string;
  email: string;
}

interface FormData {
  consultant: string;
  consultant_mail: string;
  coordinators: Coordinator[];
  managers: Manager[];
  distributors: any[];
  guid: string;
}

type ConsultantModalProps = {
  isOpen: boolean;
  consultant: any;
  distributors: any[];
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
};

export const ConsultantModal = ({
  isOpen,
  consultant,
  distributors,
  onClose,
  onSave,
}: ConsultantModalProps) => {
  const distributorsList = useDistributors();
  const [formData, setFormData] = useState<FormData>({
    consultant: '',
    consultant_mail: '',
    coordinators: [],
    managers: [],
    distributors: [],
    guid: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  // Função para converter dados antigos para novo formato
  const convertLegacyData = (consultant: any): FormData => {
    const coordinators: Coordinator[] = [];
    const managers: Manager[] = [];


    // Priorizar coordenadores do novo formato, se não existir usar formato antigo
    if (consultant.coordinators && consultant.coordinators !== '[]') {
      let coordinatorsData = consultant.coordinators;
      
      // Se é string JSON, fazer parse
      if (typeof coordinatorsData === 'string') {
        try {
          coordinatorsData = JSON.parse(coordinatorsData);
        } catch (error) {
          console.warn('Erro ao fazer parse dos coordenadores:', error);
          coordinatorsData = [];
        }
      }
      
      // Se agora é um array, processar
      if (Array.isArray(coordinatorsData)) {
        coordinators.push(...coordinatorsData.map((coord: any) => ({
          name: coord.name || '',
          email: coord.email || ''
        })));
      }
    } else if (consultant.coordinator || consultant.coordinator_mail) {
      // Usar formato antigo apenas se não existir novo formato
      coordinators.push({
        name: consultant.coordinator || '',
        email: consultant.coordinator_mail || ''
      });
    }

    // Priorizar gerentes do novo formato, se não existir usar formato antigo
    if (consultant.managers && consultant.managers !== '[]') {
      let managersData = consultant.managers;
      
      // Se é string JSON, fazer parse
      if (typeof managersData === 'string') {
        try {
          managersData = JSON.parse(managersData);
        } catch (error) {
          console.warn('Erro ao fazer parse dos gerentes:', error);
          managersData = [];
        }
      }
      
      // Se agora é um array, processar
      if (Array.isArray(managersData)) {
        managers.push(...managersData.map((manager: any) => ({
          name: manager.name || '',
          email: manager.email || ''
        })));
      }
    } else if (consultant.manager || consultant.manager_mail) {
      // Usar formato antigo apenas se não existir novo formato
      managers.push({
        name: consultant.manager || '',
        email: consultant.manager_mail || ''
      });
    }

    return {
      consultant: consultant.consultant || '',
      consultant_mail: consultant.consultant_mail || '',
      coordinators,
      managers,
      guid: consultant.guid || '',
      distributors: consultant.distributors?.map((dist: any) => ({
        value: dist.value,
        label: dist.label,
      })) || [],
    };
  };

  useEffect(() => {
    if (consultant) {
      setFormData(convertLegacyData(consultant));
    } else {
      setFormData({
        consultant: '',
        consultant_mail: '',
        coordinators: [],
        managers: [],
        distributors: [],
        guid: ''
      });
    }
  }, [consultant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onSave(formData);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCoordinatorsChange = (coordinators: Coordinator[]) => {
    setFormData({ ...formData, coordinators });
  };

  const handleManagersChange = (managers: Manager[]) => {
    setFormData({ ...formData, managers });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {consultant ? 'Editar Consultor' : 'Novo Consultor'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Dados do Consultor */}
            <section className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Dados do Consultor
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Consultor *
                  </label>
                  <input
                    type="text"
                    value={formData.consultant}
                    onChange={(e) => setFormData({ ...formData, consultant: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite o nome do consultor"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail do Consultor *
                  </label>
                  <input
                    type="email"
                    value={formData.consultant_mail}
                    onChange={(e) => setFormData({ ...formData, consultant_mail: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite o e-mail do consultor"
                    required
                  />
                </div>
              </div>
            </section>

            {/* Coordenadores */}
            <section className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Coordenadores
              </h4>
              <CoordinatorSection
                coordinators={formData.coordinators}
                onChange={handleCoordinatorsChange}
              />
            </section>

            {/* Gerentes */}
            <section className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Gerentes
              </h4>
              <ManagerSection
                managers={formData.managers}
                onChange={handleManagersChange}
              />
            </section>

            {/* Distribuidores */}
            <section className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Distribuidores
              </h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Selecione os Distribuidores
                </label>
                <Select
                  isMulti
                  value={formData.distributors}
                  onChange={(selected: any) => setFormData({ ...formData, distributors: selected || [] })}
                  options={distributorsList}
                  className="w-full"
                  placeholder="Selecione os distribuidores"
                  noOptionsMessage={() => "Nenhum distribuidor encontrado"}
                  loadingMessage={() => "Carregando..."}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                      '&:hover': {
                        borderColor: '#9ca3af',
                      },
                      '&:focus-within': {
                        borderColor: '#3b82f6',
                        boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                      },
                    }),
                  }}
                />
              </div>
            </section>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 font-medium"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Salvar</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};